import React, {useState, useEffect, useContext} from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  View,
} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';

import Button from '../../components/elements/Button';
import Header from '../../components/elements/Header';
import Divider from '../../components/elements/Divider';
import Popup from '../../components/elements/Popup';
import Text from '../../components/elements/Text';
import Input from '../../components/elements/Input';

import makeStyles from './styles';
import {useTheme, useThemedStyles} from '../../theme';
import {BUTTON_TYPES} from '../../utils/Constants';
import {
  isEmpty,
  removeAllLocalData,
  validPassword,
} from '../../utils/HelperMethods';
import {fetchApi} from '../../utils/ApiHelper';
import {CHANGE_PASSWORD, HTTP_METHODS} from '../../utils/URLConstants';
import {loadingPauseContext} from '../../contexts/LoadingPauseContext';
import {errorStatusContext} from '../../contexts/ErrorStatusContext';
import {CommonActions} from '@react-navigation/native';
import UI from './ui';
import CommonStyles from '../../utils/CommonStyles';
import {tID} from '../../utils/TestIdHelper';
import {ClosedEyeIcon, OpenEyeIcon} from '../../utils/SvgIcons';
import {shopContext} from '../../contexts/ShopContext';

export const SuccessPopup = ({showSuccesModal, onPressFinish = () => {}}) => {
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  return (
    <Popup
      testID={tID('ChangePassword-Popup-Success')}
      visible={showSuccesModal}
      textAlign="center"
      title="Success!"
      description={'Your password has successfully been reset.'}>
      <Button
        testID={tID('ChangePassword-Button-ReturnToSignin')}
        style={styles.button}
        buttonType={BUTTON_TYPES.Filled}
        titleColor={colors.textOnAccent}
        onPress={onPressFinish}
        title={'Return to Sign-in'}
      />
    </Popup>
  );
};

const ChangePassword = ({
  navigation,
  route,
  openPopup,
  onClosePopup = () => {},
}) => {
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  const [showSuccesModal, setshowSuccesModal] = useState(false);
  const [password, setPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const [isValidForm, setValidForm] = useState(false);
  const [hasPasswordError, setHasPasswordError] = useState(false);
  const [hasRepeatPasswordError, setHasRepeatPasswordError] = useState(false);

  const lpc = useContext(loadingPauseContext);
  const esc = useContext(errorStatusContext);
  const ShopC = useContext(shopContext);

  useEffect(() => {
    validatePasswordRecovery();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- validation intentionally keyed to password fields only
  }, [password, repeatPassword]);

  const onPressShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const onPressShowRepeatPassword = () => {
    setShowRepeatPassword(!showRepeatPassword);
  };

  const onPressFinish = async () => {
    onClosePopup();
    setshowSuccesModal(false);
    if (Platform.OS === 'web') {
      navigation('/login');
    } else {
      navigation.dispatch(
        CommonActions.reset({
          index: 0,
          routes: [{name: 'OnBoardingExperience'}],
        }),
      );
    }
  };

  const validatePasswordRecovery = () => {
    if (isEmpty(password) || isEmpty(repeatPassword)) {
      setValidForm(false);
      return;
    }

    setValidForm(true);
  };

  const onPressConfirm = () => {
    if (!validPassword(password)) {
      setHasPasswordError(true);
      return;
    } else {
      setHasPasswordError(false);
    }

    if (password !== repeatPassword) {
      setHasRepeatPasswordError(true);
      return;
    } else {
      setHasRepeatPasswordError(false);
    }

    const postData = {
      password: password,
      repeatPassword: repeatPassword,
    };
    lpc.setVisible(true);
    fetchApi(HTTP_METHODS.POST, CHANGE_PASSWORD, postData)
      .then(response => {
        lpc.setVisible(false);
        if (response.status == 200) showSuccessPopup();
      })
      .catch(error => {
        lpc.setVisible(false);
        esc.showError(error);
        // if (error.data) {
        //   if (error.data.errors instanceof Array) {
        //     alert(error.data.errors[0].message);
        //   } else if (typeof error.data.errors === 'string') {
        //     alert(error.data.errors);
        //   }
        // }
      });
  };

  async function showSuccessPopup() {
    await removeAllLocalData(ShopC);
    setTimeout(() => {
      setshowSuccesModal(true);
    }, 1000);
  }

  return (
    <UI
      showSuccesModal={showSuccesModal}
      onPressFinish={onPressFinish}
      openPopup={openPopup}>
      <SafeAreaView
        testID={tID('ChangePassword-SafeAreaView-Main')}
        style={[
          styles.screenBackground,
          Platform.OS === 'web' ? styles.webcontainer : CommonStyles.full_flex,
        ]}>
        <View
          testID={tID('ChangePassword-View-HeaderContainer')}
          style={styles.headerView}>
          <Header
            title="Change Password"
            onPressLeftIcon={navigation?.goBack}
            hideLeftIem={Platform.OS === 'web'}
          />
        </View>
        <KeyboardAvoidingView
          testID={tID('ChangePassword-KeyboardAvoidingView-1')}
          behavior={Platform.OS === 'ios' ? 'padding' : null}
          style={CommonStyles.full_flex}
          // keyboardVerticalOffset={Platform.OS === "ios" ? 64 : 0}
        >
          <ScrollView keyboardShouldPersistTaps="handled"
            testID={tID('ChangePassword-ScrollView-1')}
            style={styles.scrollView}
            contentContainerStyle={styles.scrollviewContainer}>
            <Text
              testID={tID('ChangePassword-Text-Description')}
              variation="Text1Regular"
              color={colors.textSecondary}>
              Password must be at least 8 characters and contain at least one
              capital letter, one special character, one number, and cannot be a
              common password.
            </Text>
            <Divider top={16} />

            <Input
              testID={tID('ChangePassword-Input-NewPassword')}
              hasError={hasPasswordError}
              errorText={'Password doesn’t meet the requirements.'}
              secureTextEntry={!showPassword}
              inputTitle={'New Password'}
              placeholder={'Type here...'}
              value={password}
              onChangeText={setPassword}
              topMargin={24}
              rightIcon={showPassword ? <OpenEyeIcon /> : <ClosedEyeIcon />}
              onPressRightIcon={onPressShowPassword}
              textContentType="newPassword"
              autoComplete="new-password"
            />

            <Input
              testID={tID('ChangePassword-Input-RepeatPassword')}
              hasError={hasRepeatPasswordError}
              errorText={'Passwords do not match.'}
              secureTextEntry={!showRepeatPassword}
              inputTitle={'Repeat Password'}
              placeholder={'Type here...'}
              value={repeatPassword}
              onChangeText={setRepeatPassword}
              topMargin={24}
              rightIcon={
                showRepeatPassword ? <OpenEyeIcon /> : <ClosedEyeIcon />
              }
              onPressRightIcon={onPressShowRepeatPassword}
              textContentType="newPassword"
              autoComplete="new-password"
            />
          </ScrollView>
        </KeyboardAvoidingView>
        <View
          testID={tID('ChangePassword-View-ButtonsContainer')}
          style={styles.buttonContainer}>
          {Platform.OS === 'web' ? (
            <Button
              testID={tID('ChangePassword-Button-Cancel')}
              style={[styles.button, CommonStyles.mr_16]}
              onPress={onClosePopup}
              title={'Cancel'}
            />
          ) : null}
          <Button
            testID={tID('ChangePassword-Button-Save')}
            disabled={!isValidForm}
            buttonType={BUTTON_TYPES.Filled}
            titleColor={colors.textOnAccent}
            title={
              Platform.OS === 'web' ? 'Save Changes' : 'Confirm New Password'
            }
            onPress={onPressConfirm}
            style={styles.button}
          />
        </View>
        {Platform.OS !== 'web' ? (
          <SuccessPopup
            showSuccesModal={showSuccesModal}
            onPressFinish={onPressFinish}
          />
        ) : null}
      </SafeAreaView>
    </UI>
  );
};

export default ChangePassword;
